/*Licensing
This component was built from the works of "zygisS22" on Github: https://github.com/zygisS22/color-palette-extraction

MIT License

Copyright (c) 2022 Zygimantas Sniurevicius

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction,
 including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useState } from "react";
import exampleData from "../../Assets/paletteExampleData.json";
import "../../css/colorPalette.css";
import "../../css/quiltCalcStyles.css";
import { Helmet } from "react-helmet";
import NavBar from "../NavBar/NavBar.js";
import { Footer } from "../Footer/Footer.js";
import RecirculationUnit from "../RecirculationUnit/RecirculationUnit.js";
import ReactGA from "react-ga4";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { PDFDocument } from "./PDFDocument/PDFDocument.js";

const ColorPaletteCreator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadMessage, setIsLoadMessage] = useState("Loading...");
  const [colors, setColors] = useState([]);
  const [compColors, setCompColors] = useState([]);
  const [savedColors, setSavedColors] = useState([]);
  const [imgFile, setImgFile] = useState();
  const [pdfDownloadReady, setPDFDownloadReady] = useState(0);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState("");

  const handleSwatchSave = (hexColor) => {
    setDownloadErrorMessage("");

    ReactGA.event({
      category: "ColorPaletteCreator",
      action: "swatch-save",
      label: hexColor, // optional
    });

    //catch duplicate save
    if (document.getElementById(hexColor + "-save").textContent === "Saved!") {
      return;
    }

    setSavedColors((prevState) => {
      // Check if the color is already in the array
      if (!prevState.includes(hexColor)) {
        // If not, add the new color
        return [...prevState, hexColor];
      }
      // Otherwise, return the previous state
      return prevState;
    });
    document.getElementById(hexColor + "-save").textContent = "Saved!";
  };

  const handleSwatchDelete = (hexColor) => {
    let tempSaved = savedColors.filter((c) => c !== hexColor);
    setSavedColors(tempSaved);

    //reset saved button
    document.getElementById(hexColor + "-save").textContent = "Save";
  };

  const handleSaveAllSwatches = () => {
    setDownloadErrorMessage("");
    setSavedColors(colors);
  };

  function PaletteItemMenu({ hexColor, buttonFunction }) {
    let buttonElement;
    switch (buttonFunction) {
      case "Save":
        buttonElement = (
          <button
            id={hexColor + "-save"}
            onClick={() => handleSwatchSave(hexColor)}
          >
            Save
          </button>
        );
        break;
      case "Remove":
        buttonElement = (
          <button onClick={() => handleSwatchDelete(hexColor)}>Remove</button>
        );
        break;
      default:
        console.log("No ListElement Menu button found for: " + buttonFunction);
        break;
    }

    return (
      <div className="menu" key={hexColor}>
        <span>...</span>
        <ul className="menu-items">
          <li>{buttonElement}</li>
        </ul>
      </div>
    );
  }

  function PaletteItem({ hexColor, buttonFunction }) {
    return (
      <div className="listElement" key={hexColor}>
        <PaletteItemMenu hexColor={hexColor} buttonFunction={buttonFunction} />
        <div>{hexColor}</div>
        <div className="swatch" style={{ backgroundColor: hexColor }}></div>
      </div>
    );
  }

  const buildPalette = (colorsList) => {
    let tempColors = [];
    let tempCompColors = [];
    let printedColors = [];
    let printedCompColors = [];

    const orderedByColor = orderByLuminance(colorsList);
    const hslColors = convertRGBtoHSL(orderedByColor);

    for (let i = 0; i < orderedByColor.length; i++) {
      const hexColor = rgbToHex(orderedByColor[i]);

      const hexColorComplementary = hslToHex(hslColors[i]);

      if (i > 0) {
        const difference = calculateColorDifference(
          orderedByColor[i],
          orderedByColor[i - 1],
        );

        if (difference < 120) {
          continue;
        }
      }
      tempColors = tempColors.concat(hexColor);
      printedColors = printedColors.concat(hexColor);

      if (hslColors[i].h) {
        tempCompColors = tempCompColors.concat(hexColorComplementary);
        printedCompColors = printedCompColors.concat(hexColorComplementary);
      }
    }
    console.log("Colors: " + JSON.stringify(printedColors));
    console.log("Comp Colors: " + JSON.stringify(printedCompColors));
    setColors(tempColors);
    setCompColors(tempCompColors);
  };

  const rgbToHex = (pixel) => {
    const componentToHex = (c) => {
      const hex = c.toString(16);
      return hex.length == 1 ? "0" + hex : hex;
    };

    return (
      "#" +
      componentToHex(pixel.r) +
      componentToHex(pixel.g) +
      componentToHex(pixel.b)
    ).toUpperCase();
  };

  /**
   * Convert HSL to Hex
   * this entire formula can be found in stackoverflow, credits to @icl7126 !!!
   * https://stackoverflow.com/a/44134328/17150245
   */
  const hslToHex = (hslColor) => {
    const hslColorCopy = { ...hslColor };
    hslColorCopy.l /= 100;
    const a =
      (hslColorCopy.s * Math.min(hslColorCopy.l, 1 - hslColorCopy.l)) / 100;
    const f = (n) => {
      const k = (n + hslColorCopy.h / 30) % 12;
      const color =
        hslColorCopy.l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
  };

  /**
   * Convert RGB values to HSL
   * This formula can be
   * found here https://www.niwa.nu/2013/05/math-behind-colorspace-conversions-rgb-hsl/
   */
  const convertRGBtoHSL = (rgbValues) => {
    return rgbValues.map((pixel) => {
      let hue,
        saturation,
        luminance = 0;

      // first change range from 0-255 to 0 - 1
      let redOpposite = pixel.r / 255;
      let greenOpposite = pixel.g / 255;
      let blueOpposite = pixel.b / 255;

      const Cmax = Math.max(redOpposite, greenOpposite, blueOpposite);
      const Cmin = Math.min(redOpposite, greenOpposite, blueOpposite);

      const difference = Cmax - Cmin;

      luminance = (Cmax + Cmin) / 2.0;

      if (luminance <= 0.5) {
        saturation = difference / (Cmax + Cmin);
      } else if (luminance >= 0.5) {
        saturation = difference / (2.0 - Cmax - Cmin);
      }

      /**
       * If Red is max, then Hue = (G-B)/(max-min)
       * If Green is max, then Hue = 2.0 + (B-R)/(max-min)
       * If Blue is max, then Hue = 4.0 + (R-G)/(max-min)
       */
      const maxColorValue = Math.max(pixel.r, pixel.g, pixel.b);

      if (maxColorValue === pixel.r) {
        hue = (greenOpposite - blueOpposite) / difference;
      } else if (maxColorValue === pixel.g) {
        hue = 2.0 + (blueOpposite - redOpposite) / difference;
      } else {
        hue = 4.0 + (greenOpposite - blueOpposite) / difference;
      }

      hue = hue * 60; // find the sector of 60 degrees to which the color belongs

      // it should be always a positive angle
      if (hue < 0) {
        hue = hue + 360;
      }

      // When all three of R, G and B are equal, we get a neutral color: white, grey or black.
      if (difference === 0) {
        return false;
      }

      return {
        h: Math.round(hue) + 180, // plus 180 degrees because that is the complementary color
        s: parseFloat(saturation * 100).toFixed(2),
        l: parseFloat(luminance * 100).toFixed(2),
      };
    });
  };

  /**
   * Using relative luminance we order the brightness of the colors
   * the fixed values and further explanation about this topic
   * can be found here -> https://en.wikipedia.org/wiki/Luma_(video)
   */
  const orderByLuminance = (rgbValues) => {
    const calculateLuminance = (p) => {
      return 0.2126 * p.r + 0.7152 * p.g + 0.0722 * p.b;
    };

    return rgbValues.sort((p1, p2) => {
      return calculateLuminance(p2) - calculateLuminance(p1);
    });
  };

  const buildRgb = (imageData) => {
    const rgbValues = [];
    // note that we are loopin every 4!
    // for every Red, Green, Blue and Alpha
    for (let i = 0; i < imageData.length; i += 4) {
      const rgb = {
        r: imageData[i],
        g: imageData[i + 1],
        b: imageData[i + 2],
      };

      rgbValues.push(rgb);
    }

    return rgbValues;
  };

  /**
   * Calculate the color distance or difference between 2 colors
   *
   * further explanation of this topic
   * can be found here -> https://en.wikipedia.org/wiki/Euclidean_distance
   * note: this method is not accuarate for better results use Delta-E distance metric.
   */
  const calculateColorDifference = (color1, color2) => {
    const rDifference = Math.pow(color2.r - color1.r, 2);
    const gDifference = Math.pow(color2.g - color1.g, 2);
    const bDifference = Math.pow(color2.b - color1.b, 2);

    return rDifference + gDifference + bDifference;
  };

  const findBiggestColorRange = (rgbValues) => {
    /**
     * Min is initialized to the maximum value posible
     * from there we procced to find the minimum value for that color channel
     *
     * Max is initialized to the minimum value posible
     * from there we procced to fin the maximum value for that color channel
     */
    let rMin = Number.MAX_VALUE;
    let gMin = Number.MAX_VALUE;
    let bMin = Number.MAX_VALUE;

    let rMax = Number.MIN_VALUE;
    let gMax = Number.MIN_VALUE;
    let bMax = Number.MIN_VALUE;

    rgbValues.forEach((pixel) => {
      rMin = Math.min(rMin, pixel.r);
      gMin = Math.min(gMin, pixel.g);
      bMin = Math.min(bMin, pixel.b);

      rMax = Math.max(rMax, pixel.r);
      gMax = Math.max(gMax, pixel.g);
      bMax = Math.max(bMax, pixel.b);
    });

    const rRange = rMax - rMin;
    const gRange = gMax - gMin;
    const bRange = bMax - bMin;

    // determine which color has the biggest difference
    const biggestRange = Math.max(rRange, gRange, bRange);
    if (biggestRange === rRange) {
      return "r";
    } else if (biggestRange === gRange) {
      return "g";
    } else {
      return "b";
    }
  };

  /**
   * Median cut implementation
   * can be found here -> https://en.wikipedia.org/wiki/Median_cut
   */
  const quantization = (rgbValues, depth) => {
    const MAX_DEPTH = 4;

    // Base case
    if (depth === MAX_DEPTH || rgbValues.length === 0) {
      const color = rgbValues.reduce(
        (prev, curr) => {
          prev.r += curr.r;
          prev.g += curr.g;
          prev.b += curr.b;

          return prev;
        },
        {
          r: 0,
          g: 0,
          b: 0,
        },
      );

      color.r = Math.round(color.r / rgbValues.length);
      color.g = Math.round(color.g / rgbValues.length);
      color.b = Math.round(color.b / rgbValues.length);

      return [color];
    }

    /**
     *  Recursively do the following:
     *  1. Find the pixel channel (red,green or blue) with biggest difference/range
     *  2. Order by this channel
     *  3. Divide in half the rgb colors list
     *  4. Repeat process again, until desired depth or base case
     */
    const componentToSortBy = findBiggestColorRange(rgbValues);
    rgbValues.sort((p1, p2) => {
      return p1[componentToSortBy] - p2[componentToSortBy];
    });

    const mid = rgbValues.length / 2;
    return [
      ...quantization(rgbValues.slice(0, mid), depth + 1),
      ...quantization(rgbValues.slice(mid + 1), depth + 1),
    ];
  };

  const handlePDFDownload = () => {
    //Error handling:
    if (imgFile == undefined) {
      setDownloadErrorMessage(
        "Please upload an image or select an example image to create a palette. Make sure to save your favorites from the generated palette.",
      );
      return;
    }
    if (savedColors.length === 0) {
      console.log(JSON.stringify(colors));
      setDownloadErrorMessage(
        "Please save colors before downloading your palette.",
      );
      return;
    }

    //get PDF, set PDF for link to see
    setPDFDownloadReady(
      <PDFDocument paletteColors={savedColors} imageSRC={imgFile} />,
    );
  };

  const main = () => {
    setDownloadErrorMessage("");

    ReactGA.event({
      category: "ColorPaletteCreator",
      action: "file-upload",
    });
    setColors([]);
    setCompColors([]);
    setIsLoadMessage("Uploading file...");
    setIsLoading(true);
    const imgFile = document.getElementById("imgfile");
    const image = new Image();
    const file = imgFile.files[0];

    if (!new RegExp("image/*").test(file.type)) {
      ReactGA.event({
        category: "ColorPaletteCreator",
        action: "file-upload-failure",
      });
      setIsLoadMessage("Error: Please upload an image file type");
      return;
    } else if (file.size > 4 * 1024 * 1024) {
      setIsLoadMessage(
        "Error: The file size is too large. Please keep your image sizes under 4MB",
      );
    }

    const fileReader = new FileReader();

    fileReader.onload = () => {
      image.onload = () => {
        const canvas = document.getElementById("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, image.width, image.height);

        const imageData = ctx.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        ).data;
        setImgFile(canvas.toDataURL());
        setIsLoadMessage("Getting image colors...");
        const rgbArray = buildRgb(imageData);

        const quantColors = quantization(rgbArray, 0);
        setIsLoadMessage("Building Palette...");
        buildPalette(quantColors);
        setIsLoading(false);
      };
      image.src = fileReader.result;
    };
    fileReader.readAsDataURL(file);
  };

  const handleExampleImageClick = (imgKey) => {
    setDownloadErrorMessage("");

    ReactGA.event({
      category: "ColorPaletteCreator",
      action: "example-image-click",
      label: imgKey, // optional
    });

    setColors([]);
    setCompColors([]);
    setIsLoadMessage("");
    var imgFrame = new Image();
    imgFrame.src = process.env.REACT_APP_URL + `PaletteImages/${imgKey}.jpg`;

    setImgFile({
      uri: imgFrame.src,
      method: "GET",
      headers: { "Cache-Control": "no-cache" },
      body: "",
    });

    imgFrame.onload = function () {
      const canvas = document.getElementById("canvas");
      canvas.width = imgFrame.width;
      canvas.height = imgFrame.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imgFrame, 0, 0);
    };
    setColors(exampleData[imgKey].colors);
    setCompColors(exampleData[imgKey].compColors);
  };

  // Map over your image data and generate JSX for each image
  const exampleImages = Object.keys(exampleData).map((imageName) => {
    const imagePath =
      process.env.REACT_APP_URL + `PaletteImages/${imageName}.jpg`;
    return (
      <img
        key={imageName}
        className="exampleImage"
        id={imageName}
        alt={`Example color palette option: ${imageName}`}
        src={imagePath}
        onClick={() => handleExampleImageClick(imageName)}
      />
    );
  });

  return (
    <>
      <Helmet>
        <title>Image Color Palette Creator | Gibson Threads</title>
        <link
          rel="canonical"
          href={"https://gibsonthreads.com/color-palette-creator"}
        />
        <meta
          name="description"
          content="Creating your dream color palette for your next quilt project from your own images."
        />
        <meta property="og:title" content="Quilt Color Palette Creator" />
        <meta
          property="og:description"
          content="Upload or use an example image to create your dream color palette for your next quilt project."
        />
        <meta property="og:image" content="" />
      </Helmet>
      <NavBar displayCenter={false} title="Image Color Palette Creator" />
      <div className="colorPaletteForm basicBackground">
        <div className="description">
          Upload your image to create a color palette for fabric
          recommendations.
        </div>
        <form action="#">
          <label>
            Upload Image
            <input
              type="file"
              id="imgfile"
              alt="Input for image upload for palette processing"
              onChange={() => main()}
            />
          </label>
        </form>
        <div>{isLoading ? loadMessage : ""}</div>
        <canvas
          style={{ display: colors.length > 0 ? "block" : "none" }}
          id="canvas"
        ></canvas>
        <div style={{ display: colors.length > 0 ? "grid" : "none" }}>
          <div id="palette">
            <h2>Photo Colors</h2>
            <div className="grid-2 grid">
              {colors.map((c) => (
                <PaletteItem key={c} hexColor={c} buttonFunction={"Save"} />
              ))}
            </div>
          </div>
          <div id="complementary">
            <h2>Complementary Colors</h2>
            <div className="grid-2 grid">
              {compColors.map((c) => (
                <PaletteItem key={c} hexColor={c} buttonFunction={"Save"} />
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: savedColors.length > 0 ? "block" : "none" }}>
          <h2>Saved Palette</h2>
          <div className="grid-2 grid">
            {savedColors.map((s) => (
              <PaletteItem key={s} hexColor={s} buttonFunction={"Remove"} />
            ))}
          </div>
        </div>
        <div>
          <h2>Example Images</h2>
          <div className="grid-2 grid">{exampleImages}</div>
        </div>
        <div>
          <button onClick={() => handlePDFDownload()}>Get Download</button>

          {pdfDownloadReady === 0 ? (
            <></>
          ) : (
            <p>
              <PDFDownloadLink
                document={pdfDownloadReady}
                fileName="GibsonThreadsPalette.pdf"
              >
                {({ loading }) =>
                  loading ? "Loading document..." : "Download now!"
                }
              </PDFDownloadLink>
            </p>
          )}
          <div className="formError">
            <p>{downloadErrorMessage}</p>
            {downloadErrorMessage ===
            "Please save colors before downloading your palette." ? (
              <button onClick={() => handleSaveAllSwatches()}>
                Save all palette colors
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <RecirculationUnit />
      <Footer />
    </>
  );
};

export default ColorPaletteCreator;
